import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";

const BG = "https://images.unsplash.com/photo-1730818203797-897b2838105a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const field = "w-full rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-3 text-sm outline-none transition-colors focus:border-white/40";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form);
      nav("/");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-[#090A0C] text-white lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#090A0C] via-[#090A0C]/60 to-transparent" />
        <div className="absolute bottom-16 left-16 max-w-md">
          <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight">Scroll. Discover.<br /><span className="neon-text">Subscribe.</span></h2>
          <p className="mt-4 text-gray-300">The feed where creators sell what they love and fans get closer.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4 rise-in" data-testid={`${mode}-form`}>
          <Logo />
          <h1 className="pt-6 font-display text-2xl font-semibold tracking-tight">{isLogin ? "Welcome back" : "Join the night"}</h1>
          <p className="pb-2 text-sm text-gray-400">{isLogin ? "Log in to like, comment and subscribe." : "Create an account in seconds."}</p>
          {!isLogin && (
            <>
              <input className={field} placeholder="Display name" value={form.name} onChange={set("name")} required data-testid="signup-name-input" />
              <input className={field} placeholder="Username" value={form.username} onChange={set("username")} required data-testid="signup-username-input" />
            </>
          )}
          <input className={field} type="email" placeholder="Email" value={form.email} onChange={set("email")} required data-testid={`${mode}-email-input`} />
          <input className={field} type="password" placeholder="Password" value={form.password} onChange={set("password")} required minLength={6} data-testid={`${mode}-password-input`} />
          {error && <p className="rounded-sm border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid={isLogin ? "auth-login-submit-btn" : "auth-signup-submit-btn"}
            className="btn-neon flex w-full items-center justify-center gap-2 rounded-sm py-3.5 font-semibold text-white disabled:opacity-70">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {isLogin ? "Log in" : "Create account"}
          </button>
          <p className="text-center text-sm text-gray-400">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={isLogin ? "/signup" : "/login"} className="font-semibold text-[#FF2E4D] hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Sign up" : "Log in"}
            </Link>
          </p>
          <Link to="/" className="block text-center text-xs text-gray-500 hover:text-gray-300" data-testid="auth-back-to-feed">Back to feed</Link>
        </form>
      </div>
    </div>
  );
}
